import * as SliderPrimitive from "@radix-ui/react-slider"
import { type ComponentProps, useMemo } from "react"

import { cn } from "~/lib/utils"

function Slider({
	className,
	defaultValue,
	value,
	min = 0,
	max = 100,
	variant = "default",
	...props
}: ComponentProps<typeof SliderPrimitive.Root> & {
	variant?: "default" | "hue"
}) {
	const _values = useMemo(
		() =>
			Array.isArray(value)
				? value
				: Array.isArray(defaultValue)
					? defaultValue
					: [min, max],
		[value, defaultValue, min, max]
	)

	return (
		<SliderPrimitive.Root
			data-slot="slider"
			defaultValue={defaultValue}
			value={value}
			min={min}
			max={max}
			className={cn(
				"relative flex w-full touch-none items-center select-none data-disabled:opacity-50 data-[orientation=vertical]:h-full data-[orientation=vertical]:min-h-44 data-[orientation=vertical]:w-auto data-[orientation=vertical]:flex-col",
				className
			)}
			{...props}
		>
			<SliderPrimitive.Track
				data-slot="slider-track"
				className={cn(
					"relative grow overflow-hidden rounded-full data-[orientation=horizontal]:h-1.5 data-[orientation=horizontal]:w-full data-[orientation=vertical]:h-full data-[orientation=vertical]:w-1.5",
					variant === "hue"
						? "bg-[linear-gradient(to_right,red,yellow,lime,cyan,blue,magenta,red)]"
						: "bg-muted"
				)}
			>
				<SliderPrimitive.Range
					data-slot="slider-range"
					className={cn(
						"absolute data-[orientation=horizontal]:h-full data-[orientation=vertical]:w-full",
						variant === "hue" ? "bg-transparent" : "bg-primary"
					)}
				/>
			</SliderPrimitive.Track>
			{Array.from({ length: _values.length }, (_, index) => (
				<SliderPrimitive.Thumb
					data-slot="slider-thumb"
					key={index}
					className={cn(
						"bg-background ring-ring/50 block size-4 shrink-0 rounded-full border shadow-sm transition-[color,box-shadow] hover:ring-4 focus-visible:ring-4 focus-visible:outline-hidden disabled:pointer-events-none disabled:opacity-50",
						variant === "hue"
							? "border-2 border-white shadow-md"
							: "border-primary"
					)}
				/>
			))}
		</SliderPrimitive.Root>
	)
}

export { Slider }
